import { assign, fromPromise, setup } from "xstate";

import { apiClient } from "@/lib/api/client";

export type SelectedPlaceOption = {
  id: string; // Place.id
  value: string; // Place.id
  label: string; // Place.name
  description?: string; // Place.address or other descriptive field
  disabled?: boolean;
};

type SearchPlacesOutput = SelectedPlaceOption[];
type SearchPlacesInput = {
  searchText: string;
};

type GetPlaceOutput = SelectedPlaceOption;
type GetPlaceInput = { placeId: string };

const searchPlacesActor = fromPromise<SearchPlacesOutput, SearchPlacesInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/components/place-select", {
        params: {
          query: {
            freeText: input.searchText,
            pageSize: 5,
          },
        },
      });
      if (!res.data?.items) return [];
      return res.data.items.map((place) => ({
        id: place.id,
        value: place.id,
        label: place.name,
        description: place.description,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

const getPlaceActor = fromPromise<GetPlaceOutput, GetPlaceInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/components/place-select", {
        params: { query: { idIn: [input.placeId] } },
      });

      const place = res.data?.items[0];
      if (!place) throw new Error("Place not found");
      return {
        id: place.id,
        value: place.id,
        label: place.name,
        description: place.description,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

type Context = {
  selectedPlaceId: string;
  selectedPlaceOption: SelectedPlaceOption | null;
  searchText: string;
  searchResults: SelectedPlaceOption[];
  errorMessage: string | null;
};

type Event =
  | { type: "INIT" }
  | { type: "GET_PLACE"; placeId: string }
  | { type: "SEARCH_PLACE"; text: string }
  | {
      type: "SELECT_PLACE";
      placeId: string;
      placeOption: SelectedPlaceOption;
    }
  | { type: "REMOVE_PLACE"; placeId: string };

type Input = {
  selectedPlaceId?: string;
};

const initialContext: Context = {
  selectedPlaceId: "",
  selectedPlaceOption: null,
  searchText: "",
  searchResults: [],
  errorMessage: null,
};

export const createPlaceSelectMachine = () => {
  return setup({
    types: {
      context: {} as Context,
      events: {} as Event,
      input: {} as Input,
    },
    actors: {
      searchPlaces: searchPlacesActor,
      getPlace: getPlaceActor,
    },
    actions: {},
    guards: {
      isInitializedWithPlaceId: ({ context }) =>
        !!context.selectedPlaceId && context.selectedPlaceOption == null,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5SzAQwE4GMAWAFM6sA9gHYB0AliRQC4DEAkgHIMAqA2gAwC6ioADkVi0KpPiAAeiAIwAmAKxkAnNM7yls2QA5ZnLRoA0IAJ6JZAZgBsZeZ0sB2VbIAs0+5dmWtAX29GUGDj4hKSU1PTMbOzSvEgggsI0oiTiUghyiqruGtq6+rJGpgha0mTSJVrOSu5eskryzr7+aFh4BMTkKAA2YJhJJFDBHXQAygCiAIIASgDCABIA+rhjUyMA8kxcsQJCImJxaZbyiqruVp7meuqFiOZa5mT38vZ3deae5Y1+IAGtQ6HdXr9QbtUijMYAGTGM1YSxW602PHUST2KQOiC0RxsWnuei07mcznuNwQsjcZHc0ks9Vs7nk0juTR+LSCoM6YB6fSoIJCJDoUzGAFk1gA1MZw1YbLbI3ZJfagNKY2RlezPczSZyaSycZz2ElyB62Tx2ZyWcxEqxKJm-Vm8sgwGj-PkQUhgMIANyIAGs3Ta2naHU6EFRPZhUHKSFtpXEURHUohLESyJxpBkGZxzOoySTdKVLvY1BqSpYvF5rSz-R17WBHWy6AR0ER0GR+F1wwAzJsAWzIfqd1drvODJFD4eSUaRMdlyXjCETWmTqYU6czGmkOY0Nkc5iURPxpvknnLgUrAIr3KddBdJDdIe9vor-b9F7Zw9HEYn23i0-lkgTSZTNNLlXbMTEQXUF04XcdBecx7E1eovmaE8n3PAZLwbJsWzbGhO3QHs+zZXs0J5Do3yIMMPx4aMdkSGd0TnACl3pYCs3XMCEHkakyBAnV3jkVV5F8b4SCICA4HEQjeRlOjfzSABaSwSUU48-iIqhaBk1FZ01El8TIXV7BUeCcWkDRd1U20q0BLl0LZLS4wY-FOBsBQrHMTMoNsZwSXVexk10exKmOcozOkSzT3IQN7KnWS0QVcDLgpVMjmkBpMy8JQcwZZN5DuPQC1NBCItQk8X2k2LtIY1wXPUNKSg85xznYopnE4ZUNA8MkaTazwvl8IA */
    id: "searchPlace",
    initial: "init",
    context: ({ input }) => ({
      ...initialContext,
      selectedPlaceId: input.selectedPlaceId ?? "",
    }),
    states: {
      init: {
        on: {
          INIT: [
            {
              guard: "isInitializedWithPlaceId",
              target: "getPlace",
            },
            {
              target: "selectingPlace",
            },
          ],
        },
      },
      selectingPlace: {
        on: {
          SEARCH_PLACE: {
            actions: assign({
              searchText: ({ event }) => event.text,
            }),
            target: "searchingPlace",
          },
          SELECT_PLACE: {
            actions: assign({
              selectedPlaceId: ({ event }) => event.placeId,
              selectedPlaceOption: ({ event }) => event.placeOption,
              searchText: "",
              searchResults: [],
              errorMessage: null,
            }),
          },
          REMOVE_PLACE: {
            actions: assign({
              selectedPlaceId: () => "",
              selectedPlaceOption: null,
              errorMessage: null,
            }),
          },
        },
      },
      getPlace: {
        invoke: {
          src: "getPlace",
          input: ({ context }) => ({
            placeId: context.selectedPlaceId,
          }),
          onDone: [
            {
              target: "selectingPlace",
              actions: assign({
                selectedPlaceOption: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingPlace",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
      searchingPlace: {
        invoke: {
          src: "searchPlaces",
          input: ({ context }) => ({
            searchText: context.searchText,
          }),
          onDone: [
            {
              target: "selectingPlace",
              actions: assign({
                searchResults: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingPlace",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
    },
  });
};
